import React from "react";
import styled, { withTheme } from "styled-components";
import DefaultTable, {
  ActionButtons,
  Line,
  ExpandableLine,
} from "../../ui-library/CustomTable/DefaultTable";
import { Dropdown } from "react-bootstrap";
import TableDropDown from "../../ui-library/TableDropDown/TableDropDown";
import { useTranslation } from "react-i18next";
import {
  decimalPlaces,
  roundMealPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import { profiles, UNIT } from "../../../modules/constants";
import { isRtl } from "../../../i18n/helpers";
import TagsTooltip from "./TagsTooltip/TagsTooltip";
import SickDayIcon from "../../../assets/svg/sick-day-icon.svg";
import MissedSchoolIcon from "../../../assets/svg/missed-school-icon.svg";
import PaperRecaptureIcon from "../../../assets/svg/paper-recapture-icon.svg";
import RecallIcon from "../../../assets/svg/24h-recall-icon.svg";
import ActionsButton from "./ActionsButton/ActionsButton";
import moment from "moment";
import { getTimeFormat } from "../../../helpers/timeHelper";

const CompleteIcon = styled.i`
  color: ${(props) =>
    props?.isFastingIcon
      ? props?.theme?.blueMagenta2
      : props?.theme?.lightGreen};
  font-size: 20px;
`;

const TagsWrapper = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Tag = styled.img`
  opacity: ${(props) => props.opacity || 1};
  margin-right: 5px;

  &:last-child {
    margin-right: 0;
  }
`;

const TagIcon = styled.i`
  opacity: ${(props) => props.opacity || 1};
  margin-right: 5px;
  color: #f89400;

  &:last-child {
    margin-right: 0;
  }
`;

const StudyEntriesTable = (props) => {
  const {
    getData,
    searchedParams,
    totalitems,
    entries,
    editMeal,
    viewMeal,
    canViewActionButton,
    canCreateChangeRequestMealLog,
    canViewDetailMealLog,
    canChangeStudyStatus,
    canViewStudyStatus,
    canEditParticipant,
    canGenerateReport,
    canManageUsers,
    fetchReportEntriesData,
    loading,
    study,
    canDeleteMeal,
    roleName,
    deleteMeal,
    participant,
    refetchData,
    userData,
    canCreate24hRecall,
    canCreatePaperRecapture,
    fetchDailyTargetHistory,
  } = props;

  const { t } = useTranslation();

  const defaultProps = {
    headers: [
      { label: t("appText.date"), key: "Header1" },
      { label: t("appText.complete") },
      { label: t("appText.dietDiary.entries") },
      { label: t("appText.time") },
      {
        label: t("appText.entriesTable.tags"),
        tooltip: (
          <TagsTooltip showChangeRequestFlag={canCreateChangeRequestMealLog} />
        ),
      },
      { label: t("appText.description") },
      { label: t("appText.dietDiary.calories") },
      {
        label: isRtl()
          ? t("appText.dietDiary.mediumChainFats")
          : t("appText.dietDiary.mcf"),
      },
      {
        label: isRtl()
          ? t("appText.dietDiary.longChainFats")
          : t("appText.dietDiary.lcf"),
      },
      { label: t("appText.dietDiary.protein") },
      { label: t("appText.dietDiary.carbohydrates") },
      { label: t("appText.options") },
    ],
    maincolor: "#8cc13c",
    totalitems,
    hasSearch: 0,
    haspagesizefilter: 1,
    haspagination: 1,
    searchedParams,
    getData,
    isExpandable: true,
    loading,
  };

  const getDropDownItems = (dailyEntry) => {
    const dropDownMenuItems = {
      VIEW_DETAILS: {
        text: t("appText.viewDetails"),
        callBack: (meal) => viewMeal(meal),
        id: "viewDetails",
        isVisible: canViewDetailMealLog,
      },
      EDIT: {
        text: t("appText.edit"),
        callBack: (meal) => editMeal(meal),
        id: "edit",
        isVisible: canCreateChangeRequestMealLog && dailyEntry.completed,
      },
      REMOVE: {
        text: t("appText.remove"),
        callBack: (meal) => deleteMeal(meal),
        id: "remove",
        isVisible:
          canDeleteMeal &&
          dailyEntry.completed &&
          roleName === profiles.dietitian,
      },
    };
    return Object.values(dropDownMenuItems);
  };

  const hasPaperRecapture = (entry) => {
    return entry.meals.find((meal) => meal.paperRecapture);
  };

  const has24hRecall = (entry) => {
    return entry.meals.find((meal) => meal.is24hRecall);
  };

  const hasChangeRequestOpen = (entry) => {
    return entry.meals.find((meal) => meal.hasPendingChangeRequest);
  };

  return (
    <DefaultTable {...defaultProps} isRtl={isRtl()}>
      <ActionButtons>
        {canViewActionButton && participant && (
          <ActionsButton
            {...{ fetchReportEntriesData }}
            {...{ fetchDailyTargetHistory }}
            canChangeStudyStatus={canChangeStudyStatus}
            studyId={study}
            canViewStudyStatus={canViewStudyStatus}
            canEditParticipant={canEditParticipant}
            canGenerateReport={canGenerateReport}
            canManageUsers={canManageUsers}
            participant={participant}
            onRefreshData={refetchData}
            canCreate24hRecall={canCreate24hRecall}
            canCreatePaperRecapture={canCreatePaperRecapture}
          />
        )}
      </ActionButtons>
      {entries?.map((entry, i) => (
        <ExpandableLine key={`expandable-${entry}-${i}`}>
          <Line key={`parent-${entry}-${i}`}>
            <b>
              <span>{entry?.date}</span>
            </b>
            <span>
              {(entry.completed || entry.fastingDay) && (
                <CompleteIcon
                  isFastingIcon={entry.fastingDay}
                  className="fas fa-check-circle"
                />
              )}
            </span>
            <span>{entry.fastingDay ? "-" : entry.meals.length}</span>
            <span>-</span>
            <TagsWrapper>
              {has24hRecall(entry) && <Tag src={RecallIcon} opacity={0.5} />}
              {hasPaperRecapture(entry) && (
                <Tag src={PaperRecaptureIcon} opacity={0.5} />
              )}
              {entry.missedSchool && <Tag src={MissedSchoolIcon} />}
              {entry.sickDay && <Tag src={SickDayIcon} />}
              {canCreateChangeRequestMealLog && hasChangeRequestOpen(entry) && (
                <TagIcon className="fas fa-exchange-alt fa-lg" opacity={0.5} />
              )}
            </TagsWrapper>
            <span>{entry.fastingDay ? t("appText.fastingDayLabel") : "-"}</span>
            <span>
              {entry.fastingDay
                ? "-"
                : Number(roundMealValue(entry?.calories.value || 0)).toFixed(
                    decimalPlaces
                  )}
              {entry.fastingDay ? "" : entry?.calories.unit || "kcal"}
            </span>
            <span>
              {entry.fastingDay
                ? "-"
                : `${Number(
                    roundMealPercentage(
                      roundMealValue(entry?.mediumChainFats.percentage || 0)
                    )
                  ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                    roundMealValue(entry?.mediumChainFats.value || 0)
                  ).toFixed(decimalPlaces)}${
                    entry?.mediumChainFats?.unit || UNIT.DEFAULT
                  }`}
            </span>
            <span>
              {entry.fastingDay
                ? "-"
                : `${Number(
                    roundMealPercentage(
                      roundMealValue(entry?.longChainFats.percentage || 0)
                    )
                  ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                    roundMealValue(entry?.longChainFats.value || 0)
                  ).toFixed(decimalPlaces)}${
                    entry?.longChainFats?.unit || UNIT.DEFAULT
                  }`}
            </span>
            <span>
              {entry.fastingDay
                ? "-"
                : `${Number(
                    roundMealPercentage(
                      roundMealValue(entry?.protein.percentage || 0)
                    )
                  ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                    roundMealValue(entry?.protein.value || 0)
                  ).toFixed(decimalPlaces)}${
                    entry?.protein?.unit || UNIT.DEFAULT
                  }`}
            </span>
            <span>
              {entry.fastingDay
                ? "-"
                : `${Number(
                    roundMealPercentage(
                      roundMealValue(entry?.carbohydrates.percentage || 0)
                    )
                  ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                    roundMealValue(entry?.carbohydrates.value || 0)
                  ).toFixed(decimalPlaces)}${
                    entry?.carbohydrates?.unit || UNIT.DEFAULT
                  }`}
            </span>
            <span>-</span>
          </Line>
          {entry.fastingDay
            ? "-"
            : entry?.meals?.map(
                (child, k) =>
                  !entry.fastingDay && (
                    <Line key={`child-${child}-${k}`}>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span>
                        {child?.dateTime &&
                          moment(child.dateTime).format(
                            getTimeFormat(userData?.participantTimeFormat)
                          )}
                      </span>
                      <span>
                        {child.is24hRecall && <Tag src={RecallIcon} />}
                        {child.paperRecapture && (
                          <Tag src={PaperRecaptureIcon} />
                        )}
                        {canCreateChangeRequestMealLog &&
                          child.hasPendingChangeRequest && (
                            <TagIcon className="fas fa-exchange-alt fa-lg" />
                          )}
                      </span>
                      <span>{child?.description}</span>
                      <span>
                        {Number(
                          roundMealValue(child?.calories.value || 0)
                        ).toFixed(decimalPlaces)}
                        {child?.calories.unit || "kcal"}
                      </span>
                      <span>
                        {`${Number(
                          roundMealPercentage(
                            roundMealValue(
                              child?.mediumChainFats.percentage || 0
                            )
                          )
                        ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                          roundMealValue(child?.mediumChainFats.value || 0)
                        ).toFixed(decimalPlaces)}`}
                        {child?.mediumChainFats?.unit || UNIT.DEFAULT}
                      </span>
                      <span>
                        {`${Number(
                          roundMealPercentage(
                            roundMealValue(child?.longChainFats.percentage || 0)
                          )
                        ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                          roundMealValue(child?.longChainFats.value || 0)
                        ).toFixed(decimalPlaces)}`}
                        {child?.longChainFats?.unit || UNIT.DEFAULT}
                      </span>
                      <span>
                        {`${Number(
                          roundMealPercentage(
                            roundMealValue(child?.protein.percentage || 0)
                          )
                        ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                          roundMealValue(child?.protein.value || 0)
                        ).toFixed(decimalPlaces)}`}
                        {child?.protein?.unit || UNIT.DEFAULT}
                      </span>
                      <span>
                        {`${Number(
                          roundMealPercentage(
                            roundMealValue(child?.carbohydrates.percentage || 0)
                          )
                        ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE} - ${Number(
                          roundMealValue(child?.carbohydrates.value || 0)
                        ).toFixed(decimalPlaces)}`}
                        {child?.carbohydrates?.unit || UNIT.DEFAULT}
                      </span>
                      <TableDropDown isRtl={isRtl()}>
                        {getDropDownItems(entry)
                          .filter((dropDownItem) => dropDownItem.isVisible)
                          .map((item, index) => {
                            let disabled = false;
                            if (item.id !== "viewDetails") {
                              disabled = child.hasPendingChangeRequest;
                            }

                            return (
                              <Dropdown.Item
                                key={index}
                                bsPrefix="dropdown-item"
                                onClick={() => item.callBack(child)}
                                disabled={disabled}
                              >
                                {item.text}
                              </Dropdown.Item>
                            );
                          })}
                      </TableDropDown>
                    </Line>
                  )
              )}
        </ExpandableLine>
      ))}
    </DefaultTable>
  );
};

export default withTheme(StudyEntriesTable);
